import HeaderControls from '@components/Common/HeaderControls/HeaderControls';
import { Box, CircularProgress, FormControl, InputAdornment, InputLabel, OutlinedInput } from '@mui/material';
import { PublishIterationType, ScheduleType } from '@services/api/deckManagement/graphql/schema/API';
import { isPublishIterationApplicable } from '@utils/deckCycleHelpers';
import isEmpty from 'lodash/isEmpty';
import { HeaderControlsProps } from 'types/ScenarioManagement.types';

const ScenarioManagementHeaderControls = (props: HeaderControlsProps) => {
  const { scenarioRowFilters, setScenarioRowFilters, deckCycleOptions, isMonthApplicable } = props;

  const loadingLabel = 'Loading...';

  const handleFilterChange = (field: keyof typeof scenarioRowFilters, value: string) => {
    setScenarioRowFilters((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  // Changing the cycle resets the iteration so a stale iteration 2 selection
  // never carries over to a cycle that has no iteration concept.
  const handleDeckCycleChange = (value: string) => {
    setScenarioRowFilters((prevState) => ({
      ...prevState,
      deckCycle: value,
      publishIterationType: PublishIterationType.DEFAULT,
    }));
  };

  const showIteration = isPublishIterationApplicable(scenarioRowFilters.deckCycle);

  const loadingInput = (
    <OutlinedInput
      value='Loading...'
      size='small'
      label='Deck Cycle'
      readOnly
      fullWidth
      startAdornment={
        <InputAdornment position='start'>
          <CircularProgress size='1rem' />
        </InputAdornment>
      }
    />
  );

  const selects = [
    {
      label: 'Type',
      value: scenarioRowFilters.scheduleType,
      options: [
        { value: ScheduleType.APPROVED, label: 'Approved Schedule' },
        { value: ScheduleType.MODIFIED, label: 'Modified Schedule' },
      ],
      onChange: (value: string) => handleFilterChange('scheduleType', value),
    },
    ...(isMonthApplicable
      ? [
          {
            label: 'Month',
            value: scenarioRowFilters.month,
            options: [
              { value: '1', label: 'January' },
              { value: '2', label: 'February' },
              { value: '3', label: 'March' },
              { value: '4', label: 'April' },
              { value: '5', label: 'May' },
              { value: '6', label: 'June' },
              { value: '7', label: 'July' },
              { value: '8', label: 'August' },
              { value: '9', label: 'September' },
              { value: '10', label: 'October' },
              { value: '11', label: 'November' },
              { value: '12', label: 'December' },
            ],
            onChange: (value: string) => handleFilterChange('month', value),
          },
        ]
      : []),
    {
      label: isEmpty(deckCycleOptions) ? loadingLabel : 'Deck Cycle',
      value: scenarioRowFilters.deckCycle || '',
      options: deckCycleOptions.map((cycle) => ({ value: cycle, label: cycle })),
      disabled: isEmpty(deckCycleOptions),
      readOnly: isEmpty(deckCycleOptions),
      onChange: handleDeckCycleChange,
    },
  ];

  const iterationSelect = {
    label: 'Iteration',
    value: scenarioRowFilters.publishIterationType ?? PublishIterationType.DEFAULT,
    options: [
      { value: PublishIterationType.DEFAULT, label: '1' },
      { value: PublishIterationType.AWS_REFRESH, label: '2' },
    ],
    onChange: (value: string) => handleFilterChange('publishIterationType', value),
  };

  const deckCycleSelect = selects[selects.length - 1];
  const filterSelects = selects.slice(0, -1);

  return (
    <Box className='header-controls'>
      <HeaderControls selects={filterSelects} />
      <FormControl className='select-control'>
        <InputLabel>Deck Cycle</InputLabel>
        {isEmpty(deckCycleOptions) ? loadingInput : <HeaderControls selects={[deckCycleSelect]} />}
      </FormControl>
      {showIteration && <HeaderControls selects={[iterationSelect]} />}
    </Box>
  );
};

export default ScenarioManagementHeaderControls;
